import initCarousel from './modules/carousel'

declare global {
    // registered by the DTN widget scripts loaded in _wrappers/site/site.twig
    interface Window { dtn: any }
}

class CookieHelper {
    static setCookie(name: string, value: string, daysTillExpire: number, path: string = '/'): void {
        const date: Date = new Date();
        date.setTime(date.getTime() + (daysTillExpire * 24 * 60 * 60 * 1000));
        document.cookie = `${name}=${value};expires=${date.toUTCString()};path=${path};`;
    }

    static getCookie(cname: string): string {
        const name = cname + "=";
        for (let c of decodeURIComponent(document.cookie).split(';')) {
            c = c.trimStart();
            if (c.indexOf(name) === 0) {
                return c.substring(name.length);
            }
        }
        return "";
    }
}

const DEFAULT_CASH_BIDS_LOCATION = 'Sioux Center';
const DTN_NEWS_CATEGORIES = ["DTN_AG", "MARKETS", "GRAIN", "CORN", "SOYBEANS"];

const delay = (ms: number): Promise<void> => new Promise(resolve => setTimeout(resolve, ms));

// DTN widgets: give the widget scripts a moment to register on window.dtn before creating them

async function setupFutures(): Promise<void> {
    await delay(500);
    const futures = await window.dtn.futures.createFuturesTableWidget({
        "apiKey": import.meta.env.VITE_DTN_FUTURES_API_KEY,
        "container": "#dtnFutures",
        "symbol": "@S`##",
        "visibleFields": [
            "LAST",
            "CHANGE",
            "HIGH",
            "LOW",
            "LAST_UPDATE",
            "SETTLE_DATE",
            "SETTLE_PRICE"
        ],
        "groupBy": true,
        "priceFormat": "TICK",
        "timezone": "CST",
    });

    document.getElementById('dtnTickerSelect')?.addEventListener('change', (e) => {
        futures.setSymbol((e.target as HTMLSelectElement).value);
    });
}

async function setupCashBids(): Promise<void> {
    await delay(500);
    await window.dtn.cashBids.createCashBidsTableWidget({
        "apiKey": import.meta.env.VITE_DTN_API_KEY,
        "container": "#dtnCashBids",
        "siteId": "E0014201",
        "groupBy": "LOCATION",
        "showGroupByOptions": true,
        "view": "DETAIL",
        "showViewOptions": true,
        "showLocationsSelect": true,
        "showCommoditiesSelect": true,
        "defaultLocation": {
            "locationName": CookieHelper.getCookie('default_location') || DEFAULT_CASH_BIDS_LOCATION
        },
        "hideCommodities": [
            "Canola"
        ],
        "hideLocations": [
            "McNally"
        ],
        "deliveryDateFormat": "DELIVERY_LABEL",
        "visibleFields": [
            "CASH_PRICE",
            "BASIS_PRICE",
            "SYMBOL",
            "FUTURES_QUOTE",
            "FUTURES_CHANGE"
        ]
    });

    // the widget renders its locations dropdown after it resolves; remember the last pick as the default
    await delay(2000);
    const dropdownEl = document.querySelector("[label='Locations']");
    if (dropdownEl) {
        dropdownEl.insertAdjacentHTML('beforebegin', `<p class="text-xs">Your default location will always be the location you selected last!</p>`);
        dropdownEl.addEventListener('change', () => {
            CookieHelper.setCookie('default_location', (dropdownEl as any).select?.value, 7);
        });
    }
}

async function setupWeather(): Promise<void> {
    await delay(500);
    await window.dtn.localWeather.createLocalWeatherWidget({
        "apiKey": import.meta.env.VITE_DTN_WEATHER_API_KEY,
        "container": "#dtnWeather",
        "units": "Imperial",
        "defaultLocation": {
            "postalCode": "57072"
        },
        "showStationsSelect": false,
        "showPostalCodeInput": true,
        "showForecast": true,
        "showCurrentConditions": true,
        "showWeatherDetails": true,
        "callbacks": {
            "onStationChange": "",
            "onPostalCodeChange": "",
            "onWeatherChange": ""
        }
    });
}

async function setupNewsList(container: string, maximumArticleCount: number): Promise<void> {
    await delay(500);
    await window.dtn.news.createNewsListWidget({
        "apiKey": import.meta.env.VITE_DTN_NEWS_API_KEY,
        "container": container,
        "categories": DTN_NEWS_CATEGORIES,
        "maximumArticleCount": maximumArticleCount,
        "displayArticle": false
    });
}

document.addEventListener('DOMContentLoaded',
    (): void => {
        // should replace the carousel library with alpine at some point, but it's working, so we're going to leave it for now
        initCarousel();

        if (document.getElementById('dtnFutures')) setupFutures();
        if (document.getElementById('dtnCashBids')) setupCashBids();
        if (document.getElementById('dtnWeather')) setupWeather();
        if (document.getElementById('dtnNews')) setupNewsList('#dtnNews', 8);
        if (document.getElementById('dtnNewsFull')) setupNewsList('#dtnNewsFull', 100);
    })
